// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import { useState, useEffect } from "react"
import { useToast } from "@crm-src/hooks/use-toast"
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material"
import { CreditCard, Upload, X } from "lucide-react"
import { DatePicker } from "@/components/ui/date-picker"
import { invoicesApi } from "@crm-src/lib/invoices-api"

// Format a Date as yyyy-MM-dd (the contract used by the rebuilt MUI DatePicker).
const toYmd = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

interface Deal {
  id: string
  name: string
  equipment_price: number
  maintenance_percentage: number
}

interface AddInvoiceModalProps {
  isOpen: boolean
  onClose: () => void
  deal: Deal | null
  customerId: string
  onInvoiceCreated?: (invoice: any) => void
}

export function AddInvoiceModal({
  isOpen,
  onClose,
  deal,
  customerId,
  onInvoiceCreated
}: AddInvoiceModalProps) {
  const { toast } = useToast()

  // Form state — dates are kept as yyyy-MM-dd strings (the DatePicker contract).
  const [invoiceNumber, setInvoiceNumber] = useState("")
  const [issueDate, setIssueDate] = useState<string>(toYmd(new Date()))
  const [dueDate, setDueDate] = useState<string>(toYmd(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000))) // 30 days from now
  const [amount, setAmount] = useState("")
  const [paymentStatus, setPaymentStatus] = useState<"unpaid" | "paid" | "partial">("unpaid")
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  // Reset form when modal opens/closes or deal changes
  useEffect(() => {
    if (isOpen && deal) {
      // Clear invoice number and amount - let user fill them manually
      setInvoiceNumber("")
      setAmount("")
      setSelectedFile(null)

      setIssueDate(toYmd(new Date()))
      setDueDate(toYmd(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)))
      setPaymentStatus("unpaid")
    } else if (!isOpen) {
      // Reset form when modal closes
      setInvoiceNumber("")
      setAmount("")
      setSelectedFile(null)
      setIssueDate(toYmd(new Date()))
      setDueDate(toYmd(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)))
      setPaymentStatus("unpaid")
    }
  }, [isOpen, deal])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null

    if (file) {
      // Validate file size (10MB limit)
      const maxSize = 10 * 1024 * 1024 // 10MB
      if (file.size > maxSize) {
        toast({
          title: "File Too Large",
          description: "File size must be less than 10MB",
          variant: "destructive",
        })
        return
      }

      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'image/jpeg',
        'image/png',
        'image/gif'
      ]
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: "Invalid File Type",
          description: "Only PDF, DOC, DOCX, Excel, and image files are allowed",
          variant: "destructive",
        })
        return
      }
    }

    setSelectedFile(file)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!deal || !invoiceNumber || !issueDate || !dueDate || !amount) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      })
      return
    }

    const amountNum = parseFloat(amount)
    if (isNaN(amountNum) || amountNum <= 0) {
      toast({
        title: "Validation Error",
        description: "Amount must be a valid positive number",
        variant: "destructive",
      })
      return
    }

    if (dueDate < issueDate) {
      toast({
        title: "Validation Error",
        description: "Due date must be after issue date",
        variant: "destructive",
      })
      return
    }

    try {
      setIsLoading(true)

      const invoiceData = {
        invoice_number: invoiceNumber,
        issue_date: issueDate,
        due_date: dueDate,
        total_amount: amountNum,
        payment_status: paymentStatus
      }

      // Create the invoice via API
      const response = await invoicesApi.createForDeal(deal.id, customerId, invoiceData, selectedFile)

      if (!response.success) {
        throw new Error(response.errors?.[0] || 'Failed to create invoice')
      }

      toast({
        title: "Success",
        description: `Invoice ${invoiceNumber} created successfully`,
      })

      // Call the callback to refresh the data
      onInvoiceCreated?.(response.data)

      onClose()
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to create invoice",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CreditCard size={20} />
          Add Invoice
        </Box>
        <Typography sx={{ fontSize: 14, color: "text.secondary", mt: 0.5 }}>
          {deal ? `Create a new invoice for "${deal.name}"` : "Create a new invoice"}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Invoice Number */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <FormLabel htmlFor="invoice-number">Invoice Number *</FormLabel>
            <TextField
              id="invoice-number"
              size="small"
              fullWidth
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              placeholder="INV-202501-0001"
              required
            />
          </Box>

          {/* Issue Date and Due Date - Side by Side */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            {/* Issue Date */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <FormLabel>Issue Date *</FormLabel>
              <DatePicker
                value={issueDate}
                onChange={(date) => setIssueDate(date)}
                placeholder="Pick a date"
              />
            </Box>

            {/* Due Date */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <FormLabel>Due Date *</FormLabel>
              <DatePicker
                value={dueDate}
                onChange={(date) => setDueDate(date)}
                placeholder="Pick a date"
              />
            </Box>
          </Box>

          {/* Amount and Payment Status - Side by Side */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
            {/* Amount - Takes 2/3 space */}
            <Box sx={{ gridColumn: "span 2", display: "flex", flexDirection: "column", gap: 1 }}>
              <FormLabel htmlFor="amount">Amount ($) *</FormLabel>
              <TextField
                id="amount"
                type="number"
                size="small"
                fullWidth
                inputProps={{ step: "0.01", min: "0" }}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                required
              />
            </Box>

            {/* Payment Status - Takes 1/3 space */}
            <Box sx={{ gridColumn: "span 1", display: "flex", flexDirection: "column", gap: 1 }}>
              <FormLabel>Payment Status</FormLabel>
              <Select
                size="small"
                value={paymentStatus}
                onChange={(e) => setPaymentStatus(e.target.value as "unpaid" | "paid" | "partial")}
              >
                <MenuItem value="unpaid">Unpaid</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="partial">Partially Paid</MenuItem>
              </Select>
            </Box>
          </Box>

          {/* File Upload */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <FormLabel htmlFor="invoice-file">Invoice File</FormLabel>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ flex: 1 }}>
                <TextField
                  id="invoice-file"
                  type="file"
                  size="small"
                  fullWidth
                  inputProps={{ accept: ".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif" }}
                  onChange={handleFileChange}
                  sx={{ cursor: "pointer" }}
                />
              </Box>
              {selectedFile && (
                <Button
                  type="button"
                  variant="text"
                  size="small"
                  onClick={() => setSelectedFile(null)}
                  sx={{ p: 0.5, height: 32, minWidth: 32, color: "error.main", "&:hover": { color: "error.dark" } }}
                >
                  <X size={16} />
                </Button>
              )}
            </Box>
            {selectedFile && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 14, color: "text.secondary" }}>
                <Upload size={16} />
                <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selectedFile.name}</Box>
                <Box component="span" sx={{ color: "text.disabled" }}>
                  ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </Box>
              </Box>
            )}
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              Optional: PDF, DOC, DOCX, Excel, or image files (max 10MB)
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 2 }}>
          <Button
            type="button"
            variant="outlined"
            color="inherit"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? "Creating..." : "Create Invoice"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
